/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {Fragment} from 'react'
import {Listbox, ListboxButton, ListboxOptions, Transition} from '@headlessui/react'
import {ChevronUpDownIcon} from '@heroicons/react/20/solid'
import {XMarkIcon} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import {MULTISELECT_DEBOUNCE_MS, useDebouncedCallback} from 'utils/hooks/useDebounce'
import {useTranslation} from 'react-i18next'
import UiTableColumnSelectFilterOptionsList from './UiTableColumnSelectFilterComponents/UiTableColumnSelectFilterOptionsList'

interface UiTableColumnSelectFilterProps {
    options: { label: string; value: string | number }[]
    value: string | number | (string | number)[] | undefined | null
    onChange: (value: (string | number)[]) => void
    placeholder?: string
    showAllLabelWhenAllSelected?: boolean
    disabled?: boolean
    multiple?: boolean
}

const UiTableColumnSelectFilter: React.FC<UiTableColumnSelectFilterProps> = ({
    options,
    value: propValue,
    onChange,
    placeholder,
    showAllLabelWhenAllSelected = true,
    multiple = true,
    disabled = false,
}) => {
    const { t } = useTranslation()

    // Ensure value is always an array to handle legacy string values or single selections
    const value = Array.isArray(propValue) ? propValue : propValue ? [propValue] : []

    const [localValue, setLocalValue] = React.useState<(string | number)[]>(value)

    // Sync local value with prop value when it changes externally
    React.useEffect(() => {
        setLocalValue(value)
    }, [propValue]) // eslint-disable-line react-hooks/exhaustive-deps

    const debouncedOnChange = useDebouncedCallback((newValue: (string | number)[]) => {
        onChange(newValue)
    }, MULTISELECT_DEBOUNCE_MS)

    const allOptionValues = React.useMemo(
        () =>
            options
                .filter(
                    (option) =>
                        (typeof option.value === 'string' && option.value.toLowerCase() === 'all') ||
                        option.label === t('All')
                )
                .map((option) => option.value),
        [options, t]
    )

    const handleChange = (newValue: (string | number)[]) => {
        const hasAll = allOptionValues.some((allValue) => newValue.includes(allValue))

        let normalizedValue = newValue

        if (hasAll && newValue.length > 1) {
            const previouslyHadAll = allOptionValues.some((allValue) => localValue.includes(allValue))

            normalizedValue = previouslyHadAll
                ? newValue.filter((item) => !allOptionValues.some((allValue) => allValue === item))
                : [allOptionValues.find((allValue) => newValue.includes(allValue)) as string | number]
        }

        setLocalValue(normalizedValue)
        debouncedOnChange(normalizedValue)
    }

    const selectedOptions = options.filter((opt) => localValue.includes(opt.value))

    const getLabel = () => {
        if (selectedOptions.length === 0) return placeholder ?? t('All')
        if ((!multiple || disabled) && selectedOptions.length === 1) {
            return selectedOptions[0].label
        }
        if (showAllLabelWhenAllSelected && selectedOptions.length === options.length) {
            return t('Selected: {{count}}', { count: selectedOptions.length })
        }
        return t('Selected: {{count}}', { count: selectedOptions.length })
    }

    const clearSelection = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        event.stopPropagation()
        setLocalValue([])
        debouncedOnChange.cancel()
        onChange([])
    }

    return (
        <div className="UiTableColumnSelectFilter w-full mt-1.5" onClick={(e) => e.stopPropagation()}>
            <Listbox
                disabled={disabled}
                value={multiple ? localValue : (localValue[0] ?? null)}
                onChange={(nextValue) =>
                    handleChange(Array.isArray(nextValue) ? nextValue : nextValue ? [nextValue] : [])
                }
                multiple={multiple}
            >
                <div className="relative">
                    <ListboxButton
                        className={clsx(
                            'relative w-full cursor-default rounded-md border bg-white/50 py-[5px] text-left text-xs font-medium transition-all duration-150 focus:outline-none focus:border-1 focus:border-blue-400 focus:bg-white dark:bg-gray-900/50 dark:border-gray-700 dark:text-gray-100 dark:focus:bg-gray-900 border-gray-300/80',
                            localValue.length > 0
                                ? 'text-blue-500 font-semibold border-blue-300 dark:border-blue-900 pl-2 pr-6'
                                : 'text-gray-700 dark:text-gray-200 pl-2 pr-6'
                        )}
                    >
                        <span className="block truncate">{getLabel()}</span>
                        {localValue.length === 0 && (
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="size-4 text-gray-400" aria-hidden="true" />
                            </span>
                        )}
                    </ListboxButton>
                    {localValue.length > 0 && !disabled && (
                        <button
                            type="button"
                            className="absolute inset-y-0 right-0 z-10 flex items-center pr-1 text-red-500 hover:text-red-600 transition-colors"
                            onMouseDown={(event) => {
                                event.preventDefault()
                            }}
                            onClick={clearSelection}
                            aria-label={t('Clear selection')}
                            title={t('Clear selection')}
                        >
                            <XMarkIcon className="size-4" aria-hidden="true" />
                        </button>
                    )}
                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <ListboxOptions className="absolute z-[9999] mt-1 max-h-60 w-fit min-w-full overflow-auto rounded-md bg-white py-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-gray-950 dark:ring-white/10">
                            <UiTableColumnSelectFilterOptionsList options={options} />
                        </ListboxOptions>
                    </Transition>
                </div>
            </Listbox>
        </div>
    )
}

export default UiTableColumnSelectFilter
